/**
 * Marketplace browse + install — task #11.
 *
 * Two side-by-side flows on one page:
 *   1. Browse the catalog (when MARKETPLACE_DATABASE_URL is configured)
 *      and click "Install" on a verified module.
 *   2. Install from a JSON file you have on disk — drag/drop or file
 *      picker. Always available, even when the marketplace DB is not.
 *
 * Both flows post the bundle to `modules.custom.marketplace.install`,
 * which validates strictly and runs the install in one transaction.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download, Search, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/marketplace')({
  component: MarketplacePage,
})

function MarketplacePage() {
  const queryClient = useQueryClient()
  const [moduleKeyOverride, setModuleKeyOverride] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { data: catalog, isLoading } = useQuery(
    orpc.modules.custom.marketplace.list.queryOptions({ input: {} }),
  )

  const installMutation = useMutation(
    orpc.modules.custom.marketplace.install.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          `Installed '${result.moduleKey}' (${result.entityCount} entities, ${result.workflowCount} workflows, ${result.webhookCount} webhooks)`,
        )
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.modules.list.queryKey(),
        })
        setModuleKeyOverride('')
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const handleInstallFromCatalog = async (key: string) => {
    // Fetch the bundle for the catalog module, then post it to install.
    try {
      const detail = await queryClient.fetchQuery(
        orpc.modules.custom.marketplace.detail.queryOptions({ input: { key } }),
      )
      installMutation.mutate({ bundle: detail.bundle })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to fetch bundle')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const bundle = JSON.parse(String(reader.result))
        installMutation.mutate({
          bundle,
          asModuleKey: moduleKeyOverride || undefined,
        })
      } catch {
        toast.error('Invalid JSON file')
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Marketplace</h1>
        <p className="text-sm text-muted-foreground">
          Browse vetted modules from the public catalog, or install your own bundle from a JSON
          file.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Install from a JSON file</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Pick a bundle JSON exported from another tenant or shared with you. The installer
            validates the bundle and runs the install in one transaction. Webhook secrets are
            regenerated per-tenant.
          </p>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Optional: install under a different module key"
              value={moduleKeyOverride}
              onChange={(e) => setModuleKeyOverride(e.target.value)}
              className="max-w-sm"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={installMutation.isPending}
            >
              <Upload className="h-4 w-4 mr-2" />
              {installMutation.isPending ? 'Installing…' : 'Choose file'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Browse catalog</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : !catalog || catalog.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              The marketplace catalog is empty (or{' '}
              <code className="font-mono">MARKETPLACE_DATABASE_URL</code> is not configured). Use
              the file installer above to install bundles you have on disk.
            </p>
          ) : (
            <>
              {/* Search + category filter */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-48">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search modules…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    type="button"
                    variant={categoryFilter === null ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setCategoryFilter(null)}
                  >
                    All
                  </Button>
                  {Array.from(new Set(catalog.map((m) => m.category).filter(Boolean))).map(
                    (cat) => (
                      <Button
                        key={cat}
                        type="button"
                        variant={categoryFilter === cat ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat!)}
                      >
                        {cat}
                      </Button>
                    ),
                  )}
                </div>
              </div>

              {/* Filtered grid */}
              {(() => {
                const q = searchTerm.toLowerCase()
                const filtered = catalog.filter((m) => {
                  if (categoryFilter && m.category !== categoryFilter) return false
                  if (!q) return true
                  return (
                    m.name.toLowerCase().includes(q) ||
                    m.description?.toLowerCase().includes(q) ||
                    m.author.toLowerCase().includes(q) ||
                    m.key.toLowerCase().includes(q)
                  )
                })
                if (filtered.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      No modules match your search.
                    </p>
                  )
                }
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((m) => (
                      <div key={m.id} className="rounded-md border p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold">{m.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {m.author} · v{m.latestVersion}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {m.category && (
                              <Badge variant="outline" className="text-[10px]">
                                {m.category}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground tabular-nums">
                              <Download className="h-3 w-3 inline mr-0.5" />
                              {m.downloadCount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {m.description && (
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {m.description}
                          </p>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleInstallFromCatalog(m.key)}
                          disabled={installMutation.isPending}
                        >
                          Install
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
